import React, { useState } from 'react';
import { 
  Card, 
  Form, 
  InputNumber, 
  Select, 
  Button, 
  Row, 
  Col, 
  Typography, 
  Divider, 
  Space,
  Table,
  Tag,
  Statistic,
  Alert,
  Collapse
} from 'antd';
import { CalculatorOutlined, SafetyOutlined, BuildOutlined, ThunderboltOutlined, EnvironmentOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const DamCalculator = () => {
  const [form] = Form.useForm();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const slagTypeOptions = [
    { 
      label: '土石混合弃渣（18-22 kN/m³，25-35°，5-15 kPa，30-35°）', 
      value: JSON.stringify({ unitWeight: 20, frictionAngle: 30, cohesion: 10, restAngle: 32.5 }) 
    },
    { 
      label: '岩石弃渣（20-25 kN/m³，30-40°，0-5 kPa，35-40°）', 
      value: JSON.stringify({ unitWeight: 22.5, frictionAngle: 35, cohesion: 2.5, restAngle: 37.5 }) 
    },
    { 
      label: '土质弃渣（16-20 kN/m³，20-30°，10-20 kPa，25-30°）', 
      value: JSON.stringify({ unitWeight: 18, frictionAngle: 25, cohesion: 15, restAngle: 27.5 }) 
    },
    { 
      label: '砂质弃渣（17-21 kN/m³，28-35°，0-10 kPa，30-35°）', 
      value: JSON.stringify({ unitWeight: 19, frictionAngle: 31.5, cohesion: 5, restAngle: 32.5 }) 
    },
    { 
      label: '煤矸石（15-18 kN/m³，25-32°，5-12 kPa，28-33°）', 
      value: JSON.stringify({ unitWeight: 16.5, frictionAngle: 28.5, cohesion: 8.5, restAngle: 30.5 }) 
    },
  ];

  const damMaterialOptions = [
    { 
      label: '碾压土石坝（20-22 kN/m³，25-35°，20-40 kPa，0.4-0.6）', 
      value: JSON.stringify({ unitWeight: 21, frictionAngle: 30, cohesion: 30, friction: 0.5 }) 
    },
    { 
      label: '堆石坝（21-23 kN/m³，35-45°，0-10 kPa，0.5-0.7）', 
      value: JSON.stringify({ unitWeight: 22, frictionAngle: 40, cohesion: 5, friction: 0.6 }) 
    },
    { 
      label: '混凝土坝（24-25 kN/m³，0.6-0.8）', 
      value: JSON.stringify({ unitWeight: 24.5, frictionAngle: 0, cohesion: 0, friction: 0.7 }) 
    },
    { 
      label: '浆砌石坝（22-24 kN/m³，0.5-0.7）', 
      value: JSON.stringify({ unitWeight: 23, frictionAngle: 0, cohesion: 0, friction: 0.6 }) 
    },
  ];

  const foundationTypeOptions = [
    { 
      label: '岩石地基（0.6-0.8，50-200 kPa，500-2000 kPa）', 
      value: JSON.stringify({ friction: 0.7, cohesion: 125, bearingCapacity: 1250 }) 
    },
    { 
      label: '砂卵石地基（0.4-0.6，0-10 kPa，200-500 kPa）', 
      value: JSON.stringify({ friction: 0.5, cohesion: 5, bearingCapacity: 350 }) 
    },
    { 
      label: '黏土地基（0.3-0.5，20-50 kPa，100-300 kPa）', 
      value: JSON.stringify({ friction: 0.4, cohesion: 35, bearingCapacity: 200 }) 
    },
    { 
      label: '砂土地基（0.35-0.55，0-5 kPa，150-400 kPa）', 
      value: JSON.stringify({ friction: 0.45, cohesion: 2.5, bearingCapacity: 275 }) 
    },
    { 
      label: '淤泥质土地基（0.2-0.35，10-30 kPa，50-150 kPa）', 
      value: JSON.stringify({ friction: 0.275, cohesion: 20, bearingCapacity: 100 }) 
    },
  ];

  const calculateDamHeight = (slagVolume, damWidth, damLength, restAngle) => {
    const H1_method1 = slagVolume / (damWidth * damLength);
    const H1_method2 = Math.sqrt((2 * slagVolume * Math.tan(restAngle * Math.PI / 180)) / damLength);
    return { H1_method1, H1_method2 };
  };

  const calculateStability = (values) => {
    const { 
      slagVolume, 
      damWidth, 
      damLength, 
      slagParams, 
      damParams, 
      foundationParams,
      channelSlope,
      waterDepth,
      siltHeight,
      safetyHeight
    } = values;

    const slagParamsObj = typeof slagParams === 'string' ? JSON.parse(slagParams) : slagParams;
    const damParamsObj = typeof damParams === 'string' ? JSON.parse(damParams) : damParams;
    const foundationParamsObj = typeof foundationParams === 'string' ? JSON.parse(foundationParams) : foundationParams;

    const { H1_method1, H1_method2 } = calculateDamHeight(
      slagVolume, 
      damWidth, 
      damLength, 
      slagParamsObj.restAngle
    );

    const H1 = Math.max(H1_method1, H1_method2);
    const H = H1 + safetyHeight + siltHeight;

    const upstreamSlopeRatio = values.upstreamSlopeRatio;
    const downstreamSlopeRatio = values.downstreamSlopeRatio;
    const bottomWidth = damWidth + H * (1 / upstreamSlopeRatio + 1 / downstreamSlopeRatio);

    const damVolume = (damWidth + bottomWidth) * H * damLength / 2;
    const Wd = damParamsObj.unitWeight * damVolume;

    const frictionAngleRad = (45 - slagParamsObj.frictionAngle / 2) * Math.PI / 180;
    const Ka = Math.pow(Math.tan(frictionAngleRad), 2);
    const Hz = H1;
    const Pz = Ka * slagParamsObj.unitWeight * Math.pow(Hz, 2) * damLength;
    const Wz = slagParamsObj.unitWeight * slagVolume * Math.cos(channelSlope * Math.PI / 180);

    const gammaW = 9.8;
    const Ps = gammaW * Math.pow(waterDepth, 2) * damLength / 2;
    const Ws = gammaW * Math.pow(waterDepth, 2) * (1 / Math.tan(Math.atan(1 / upstreamSlopeRatio))) / 2;

    const gammaS = 9;
    const Pi = Ka * gammaS * Math.pow(siltHeight, 2) * damLength;

    const Wu = gammaW * (waterDepth + 0) * bottomWidth * damLength / 2;

    const sumW = Wd + Wz + Ws - Wu;
    const sumP = Pz + Ps + Pi;

    const Kc = (foundationParamsObj.friction * sumW + foundationParamsObj.cohesion * bottomWidth * damLength) / sumP;

    const ed = bottomWidth / 2;
    const ez = bottomWidth / 2;
    const es = bottomWidth / 2;
    const hz = Hz / 3;
    const hs = waterDepth / 3;
    const hi = siltHeight / 3;

    const M_anti = Wd * ed + Wz * ez + Ws * es;
    const M_overturn = Pz * hz + Ps * hs + Pi * hi;

    const K0 = M_anti / M_overturn;

    return {
      H, H1, H2: safetyHeight, H3: siltHeight,
      damWidth, bottomWidth,
      upstreamSlopeRatio, downstreamSlopeRatio,
      damVolume,
      Kc, K0,
      Wd, Wz, Ws, Wu, sumW,
      Pz, Ps, Pi, sumP,
      M_anti, M_overturn,
      Ka
    };
  };

  const getStabilityStatus = (value, type) => {
    if (type === 'slide') {
      if (value >= 1.2) {
        return { status: 'success', text: '满足要求', color: 'green' };
      } else if (value >= 1.05) {
        return { status: 'warning', text: '基本满足', color: 'orange' };
      } else {
        return { status: 'error', text: '不满足要求', color: 'red' };
      }
    } else if (type === 'overturn') {
      if (value >= 1.5) {
        return { status: 'success', text: '满足要求', color: 'green' };
      } else if (value >= 1.3) {
        return { status: 'warning', text: '基本满足', color: 'orange' };
      } else {
        return { status: 'error', text: '不满足要求', color: 'red' };
      }
    }
    return { status: 'error', text: '未知类型', color: 'red' };
  };

  const getOptimizationSuggestions = (result) => {
    const suggestions = [];

    if (result.Kc < 1.2) {
      suggestions.push({
        type: 'error',
        title: '抗滑稳定性不足',
        description: '抗滑系数 < 1.2，需加大坝体底宽或提高地基处理标准'
      });
    } else if (result.Kc < 1.3) {
      suggestions.push({
        type: 'warning',
        title: '抗滑稳定性偏低',
        description: '建议适当加大坝体底宽以提高安全储备'
      });
    }

    if (result.K0 < 1.5) {
      suggestions.push({
        type: 'error',
        title: '抗倾稳定性不足',
        description: '抗倾系数 < 1.5，需增加坝体重量或调整坝坡比'
      });
    } else if (result.K0 < 1.6) {
      suggestions.push({
        type: 'warning',
        title: '抗倾稳定性偏低',
        description: '建议适当调整坝坡比以提高安全储备'
      });
    }

    if (suggestions.length === 0) {
      suggestions.push({
        type: 'success',
        title: '设计合理',
        description: '各项稳定性指标均满足规范要求，设计方案可行'
      });
    }

    return suggestions;
  };

  const handleCalculate = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      console.log('表单值:', values);
      
      const result = calculateStability(values);
      console.log('计算结果:', result);
      
      setResult(result);
    } catch (error) {
      console.error('计算失败:', error);
      if (error.errorFields) {
        console.error('表单验证错误:', error.errorFields);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setResult(null);
  };

  const resultColumns = [
    {
      title: '参数',
      dataIndex: 'parameter',
      key: 'parameter',
      width: '40%',
    },
    {
      title: '数值',
      dataIndex: 'value',
      key: 'value',
      width: '30%',
      render: (value) => typeof value === 'number' ? value.toFixed(2) : value,
    },
    {
      title: '单位',
      dataIndex: 'unit',
      key: 'unit',
      width: '30%',
    },
  ];

  const resultData = result ? [
    { key: '1', parameter: '总坝高', value: result.H, unit: 'm' },
    { key: '2', parameter: '拦渣高度', value: result.H1, unit: 'm' },
    { key: '3', parameter: '安全超高', value: result.H2, unit: 'm' },
    { key: '4', parameter: '淤沙高度', value: result.H3, unit: 'm' },
    { key: '5', parameter: '坝顶宽度', value: result.damWidth, unit: 'm' },
    { key: '6', parameter: '坝底宽度', value: result.bottomWidth, unit: 'm' },
    { key: '7', parameter: '坝体体积', value: result.damVolume, unit: 'm³' },
    { key: '8', parameter: '抗滑稳定系数', value: result.Kc, unit: '-' },
    { key: '9', parameter: '抗倾稳定系数', value: result.K0, unit: '-' },
  ] : [];

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ marginBottom: '8px', color: '#1890ff' }}>
          <CalculatorOutlined /> 弃渣场拦渣坝关键参数计算器
        </Title>
        <Text type="secondary">
          计算弃渣场拦渣坝的关键设计参数，确保拦渣坝的安全性和稳定性
        </Text>
      </div>

      <Alert
        message="计算依据"
        description="本工具依据 GB 51018-2014《水土保持工程设计规范》进行计算。"
        type="info"
        showIcon
        style={{ marginBottom: '24px' }}
      />

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={14}>
          <Card title="输入参数" variant="borderless" style={{ marginBottom: '24px' }}>
            <Form form={form} layout="vertical">
              <Collapse defaultActiveKey={['1', '2', '3', '4', '5']}>
                <Panel header={<><BuildOutlined /> 弃渣信息</>} key="1">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="弃渣总量（m³）"
                        name="slagVolume"
                        rules={[{ required: true, message: '请输入弃渣总量' }]}
                      >
                        <InputNumber
                          min={1}
                          precision={2}
                          style={{ width: '100%' }}
                          placeholder="请输入弃渣总量"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="弃渣类型"
                        name="slagParams"
                        rules={[{ required: true, message: '请选择弃渣类型' }]}
                      >
                        <Select
                          placeholder="请选择弃渣类型"
                          options={slagTypeOptions}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Panel>

                <Panel header={<><EnvironmentOutlined /> 地形信息</>} key="2">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        label="沟道坡度（°）"
                        name="channelSlope"
                        rules={[{ required: true, message: '请输入沟道坡度' }]}
                      >
                        <InputNumber
                          min={0}
                          max={90}
                          precision={2}
                          style={{ width: '100%' }}
                          placeholder="请输入沟道坡度"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        label="坝体长度（m）"
                        name="damLength"
                        rules={[{ required: true, message: '请输入坝体长度' }]}
                      >
                        <InputNumber
                          min={1}
                          precision={2}
                          style={{ width: '100%' }}
                          placeholder="请输入坝体长度"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        label="坝顶宽度（m）"
                        name="damWidth"
                        rules={[{ required: true, message: '请输入坝顶宽度' }]}
                      >
                        <InputNumber
                          min={3}
                          precision={2}
                          style={{ width: '100%' }}
                          placeholder="请输入坝顶宽度"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Panel>

                <Panel header={<><SafetyOutlined /> 地质信息</>} key="3">
                  <Form.Item
                    label="地基类型"
                    name="foundationParams"
                    rules={[{ required: true, message: '请选择地基类型' }]}
                  >
                    <Select
                      placeholder="请选择地基类型"
                      options={foundationTypeOptions}
                    />
                  </Form.Item>
                </Panel>

                <Panel header={<><ThunderboltOutlined /> 水文信息</>} key="4">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        label="坝前水深（m）"
                        name="waterDepth"
                        rules={[{ required: true, message: '请输入坝前水深' }]}
                      >
                        <InputNumber
                          min={0}
                          precision={2}
                          style={{ width: '100%' }}
                          placeholder="请输入坝前水深"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        label="淤沙高度（m）"
                        name="siltHeight"
                        rules={[{ required: true, message: '请输入淤沙高度' }]}
                      >
                        <InputNumber
                          min={0}
                          precision={2}
                          style={{ width: '100%' }}
                          placeholder="请输入淤沙高度"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        label="安全超高（m）"
                        name="safetyHeight"
                        rules={[{ required: true, message: '请输入安全超高' }]}
                        initialValue={0.5}
                      >
                        <InputNumber
                          min={0.5}
                          max={1.0}
                          precision={2}
                          step={0.1}
                          style={{ width: '100%' }}
                          placeholder="请输入安全超高"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Panel>

                <Panel header="坝体信息" key="5">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="坝体材料类型"
                        name="damParams"
                        rules={[{ required: true, message: '请选择坝体材料类型' }]}
                      >
                        <Select
                          placeholder="请选择坝体材料类型"
                          options={damMaterialOptions}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="上游坝坡比（1:n）"
                        name="upstreamSlopeRatio"
                        rules={[{ required: true, message: '请输入上游坝坡比' }]}
                        initialValue={2.25}
                      >
                        <InputNumber
                          min={2.0}
                          max={2.5}
                          precision={2}
                          step={0.05}
                          style={{ width: '100%' }}
                          placeholder="请输入上游坝坡比"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="下游坝坡比（1:n）"
                        name="downstreamSlopeRatio"
                        rules={[{ required: true, message: '请输入下游坝坡比' }]}
                        initialValue={1.75}
                      >
                        <InputNumber
                          min={1.5}
                          max={2.0}
                          precision={2}
                          step={0.05}
                          style={{ width: '100%' }}
                          placeholder="请输入下游坝坡比"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Panel>
              </Collapse>

              <Divider />

              <Space>
                <Button 
                  type="primary" 
                  icon={<CalculatorOutlined />} 
                  onClick={handleCalculate}
                  loading={loading}
                  style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                >
                  开始计算
                </Button>
                <Button onClick={handleReset}>
                  重置
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          {result && (
            <>
              <Card title="计算结果" variant="borderless" style={{ marginBottom: '24px' }}>
                <Row gutter={[16, 16]}>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="总坝高"
                      value={result.H.toFixed(2)}
                      suffix="m"
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="坝底宽度"
                      value={result.bottomWidth.toFixed(2)}
                      suffix="m"
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="抗滑系数"
                      value={result.Kc.toFixed(3)}
                      valueStyle={{ color: result.Kc >= 1.2 ? '#52c41a' : '#cf1322' }}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="抗倾系数"
                      value={result.K0.toFixed(3)}
                      valueStyle={{ color: result.K0 >= 1.5 ? '#52c41a' : '#cf1322' }}
                    />
                  </Col>
                </Row>

                <Divider />

                <div style={{ marginBottom: '16px' }}>
                  <Text strong>稳定性判断：</Text>
                </div>
                <Row gutter={[16, 16]}>
                  <Col xs={12}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ marginBottom: '8px' }}>
                        <Text type="secondary">抗滑稳定性</Text>
                      </div>
                      <Tag 
                        color={getStabilityStatus(result.Kc, 'slide').color} 
                        style={{ fontSize: '14px', padding: '6px 12px' }}
                      >
                        {getStabilityStatus(result.Kc, 'slide').text}
                      </Tag>
                    </div>
                  </Col>
                  <Col xs={12}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ marginBottom: '8px' }}>
                        <Text type="secondary">抗倾稳定性</Text>
                      </div>
                      <Tag 
                        color={getStabilityStatus(result.K0, 'overturn').color} 
                        style={{ fontSize: '14px', padding: '6px 12px' }}
                      >
                        {getStabilityStatus(result.K0, 'overturn').text}
                      </Tag>
                    </div>
                  </Col>
                </Row>
              </Card>

              <Card title="详细参数表" variant="borderless" style={{ marginBottom: '24px' }}>
                <Table
                  columns={resultColumns}
                  dataSource={resultData}
                  pagination={false}
                  size="small"
                  bordered
                />
              </Card>

              <Card title="优化建议" variant="borderless">
                {getOptimizationSuggestions(result).map((suggestion, index) => (
                  <Alert
                    key={index}
                    message={suggestion.title}
                    description={suggestion.description}
                    type={suggestion.type}
                    showIcon
                    style={{ marginBottom: index < getOptimizationSuggestions(result).length - 1 ? '12px' : 0 }}
                  />
                ))}
              </Card>
            </>
          )}

          {!result && (
            <Card variant="borderless">
              <div style={{ textAlign: 'center', padding: '48px 24px' }}>
                <CalculatorOutlined style={{ fontSize: '64px', color: '#d9d9d9' }} />
                <div style={{ marginTop: '16px' }}>
                  <Text type="secondary">请输入参数并点击"开始计算"按钮</Text>
                </div>
              </div>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default DamCalculator;
